/**
 * Shared axios-lite client for the NewsPulse public API.
 * All calls are anonymous in Phase 1 (destinations verified by referer).
 * Endpoints under `/farmerspulse/me/*` require the SocialPulse JWT — we
 * pick it up from localStorage the same way AuthContext does. The
 * canonical key is `sp_token` (see /app/frontend/src/contexts/AuthContext.jsx),
 * with a `token` fallback for legacy sessions.
 */
const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/newspulse/v1`;

function _authToken() {
  if (typeof window === "undefined") return null;
  return (
    window.localStorage.getItem("sp_token") ||
    window.localStorage.getItem("token") ||
    null
  );
}

function _authHeaders() {
  const t = _authToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Exported so BreakingTicker / For You / FarmersPulse can gate UI on
// the same signal npApi uses for its requests.
export function hasNpAuthToken() {
  return Boolean(_authToken());
}

async function _get(path, params) {
  const url = new URL(BASE + path);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        // The API caps `limit` at 100 — clamp here so no caller can
        // build a 422.
        if (k === "limit") v = Math.min(Number(v) || 20, 100);
        url.searchParams.set(k, v);
      }
    });
  }
  const r = await fetch(url.toString(), {
    credentials: "omit",
    headers: { ..._authHeaders() },
  });
  if (!r.ok) throw new Error(`newspulse api ${path} → ${r.status}`);
  return r.json();
}

async function _send(path, method, body) {
  const url = new URL(BASE + path);
  const r = await fetch(url.toString(), {
    method,
    credentials: "omit",
    headers: {"Content-Type": "application/json", ..._authHeaders()},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`newspulse api ${method} ${path} → ${r.status}: ${detail.slice(0, 200)}`);
  }
  return r.json();
}

export const npApi = {
  status:        ()          => _get("/status"),
  latest:        (params)    => _get("/events/latest", params),
  breaking:      (limit=10)  => _get("/events/breaking", { limit }),
  ticker:        (limit=8)   => _get("/events/ticker", { limit }),
  localHere:     (lat, lon, limit=20) => _send(`/local/here?limit=${limit}`, "POST", { lat, lon }),
  localGeocode:  (q, limit=6)         => _get("/local/geocode", { q, limit }),
  event:         (id)        => _get(`/events/${id}`),
  eventSources:  (id)        => _get(`/events/${id}/sources`),
  byCategory:    (path, p={})=> _get(`/category/${path}`, p),
  byCountry:     (iso2, p={})=> _get(`/country/${iso2}`, p),
  byLocation:    (id, p={})  => _get(`/location/${id}`, p),
  byEntity:      (id, p={})  => _get(`/entity/${id}`, p),
  siteFeed:      (siteId, p={}) => _get(`/site/${siteId}/feed`, p),
  agentResults:  (id, p={})  => _get(`/agent/${id}/results`, p),
  categories:    ()          => _get("/categories"),
  destinations:  ()          => _get("/destinations"),
  topicAgents:   ()          => _get("/topic-agents"),
  sources:       (p={})      => _get("/sources", p),
  // Health entity graph.
  healthFeed:    (p={})      => _get("/health/feed", p),
  drugs:         (p={})      => _get("/health/drugs", p),
  diseases:      (p={})      => _get("/health/diseases", p),
  // Aviation entity graph.
  aviationFeed:  (p={})      => _get("/aviation/feed", p),
  airports:      (p={})      => _get("/aviation/airports", p),
  airlines:      (p={})      => _get("/aviation/airlines", p),
  aircraft:      (p={})      => _get("/aviation/aircraft", p),
  routes:        (p={})      => _get("/aviation/routes", p),
  airport:       (id)        => _get(`/aviation/airport/${id}`),
  airline:       (id)        => _get(`/aviation/airline/${id}`),
  // Admin Feed Builder.
  adminCreateAgent: (body)   => _send("/admin/topic-agents", "POST", body),
  adminUpdateAgent: (id, body) => _send(`/admin/topic-agents/${id}`, "PATCH", body),
  adminDeactivateAgent: (id) => _send(`/admin/topic-agents/${id}`, "DELETE"),
  // Full-text search.
  search:        (q, p={})   => _get("/search", { q, ...p }),
  // Opinion & analysis wall.
  opinion:       (p={})      => _get("/opinion", p),
  // Personalized Home — pinned sections + locations.
  prefsGet:      ()          => _get("/prefs/me"),
  prefsPut:      (body)      => _send("/prefs/me", "PUT", body),
  forYouFeed:    (p={})      => _get("/prefs/for-you", p),
  // FarmersPulse Phase 1.
  fpCropsCatalog:      ()        => _get("/farmerspulse/catalog/crops"),
  fpCommoditiesCatalog:()        => _get("/farmerspulse/catalog/commodities"),
  fpGetMyFarm:         ()        => _get("/farmerspulse/me/farm"),
  fpPutMyFarm:         (body)    => _send("/farmerspulse/me/farm", "PUT", body),
  fpDeleteMyFarm:      ()        => _send("/farmerspulse/me/farm", "DELETE"),
  fpFeed:              (p={})    => _get("/farmerspulse/feed", p),
  fpMyWeather:         ()        => _get("/farmerspulse/me/weather"),
  fpMyPrices:          ()        => _get("/farmerspulse/me/prices"),
  // Community Contributor.
  contribCategories:   ()        => _get("/contribute/categories"),
  contribSubmit:       (body)    => _send("/contribute/submit", "POST", body),
  contribMine:         ()        => _get("/contribute/mine"),
  contribGet:          (id)      => _get(`/contribute/${id}`),
  contribQueue:        (status = "pending_review", limit = 50) => _get("/admin/contribute/queue", { status, limit }),
  contribApprove:      (id)      => _send(`/admin/contribute/${id}/approve`, "POST"),
  contribReject:       (id, reason) => _send(`/admin/contribute/${id}/reject`, "POST", { reason }),
  // Reporter public profile.
  reporterProfile:     (userId, limit = 10) => _get(`/reporter/${userId}`, { limit }),
  // Enrichment layer.
  languages:           ()        => _get("/languages"),
  transliterate:       (text, lang, num = 4) => _get("/transliterate", { text, lang, num }),
  translateEvent:      (eventId, lang)   => _get(`/events/${eventId}/translate`, { lang }),
  listenEvent:         (eventId, lang = "en", voice = "nova") => _get(`/events/${eventId}/listen`, { lang, voice }),
  badgeTiers:          ()        => _get("/badges/tiers"),
  myBadge:             ()        => _get("/contribute/me/badge"),
  badgeForUser:        (userId)  => _get(`/badges/user/${userId}`),
};
